// Tope del rango de fechas del CAMBIO DE TARIFAS: el MetaEditModal del
// calendario (precio base, condición, mínimo de noches y restricciones), tanto
// en edición inline como en cambio masivo.
//
// Por qué existe (round 2026-09-12): antes de escribir nada, el backend LEE el
// calendario de Stays de cada propiedad (`applyMetaToProperty`, en
// backend/src/stays/stays.controller.js), y esa lectura se parte en tramos con
// un tope de tramos por lectura (backend/src/stays/staysCalendarioTramos.js). Un
// rango más largo no se rechazaba en el formulario: el admin lo armaba a ciegas
// y se enteraba recién al apretar «Aplicar», cuando cada propiedad volvía con
// `ok: false` y el mensaje del tope, sin escribir nada. Con esto el aviso
// aparece mientras elige las fechas y el envío queda bloqueado.
//
// El número NO se escribe suelto: sale de las MISMAS tres piezas que el backend
// —tramos por lectura × días por tramo, menos los días que la lectura agrega al
// rango editado—. Si cualquiera de las tres cambia de un lado y no del otro,
// backend/tests/stays/staysCalendarioTramos.test.js se pone rojo.
//
// Ojo: este es el tope de la LECTURA del calendario de Stays, no el de la grilla
// de /calendario (14 meses, en utils/rangoCalendario.ts). El cambio masivo no
// pasa por la grilla, así que son dos reglas distintas.
import type { Dayjs } from "dayjs";

/** `STAYS_CALENDARIO_MAX_TRAMOS`: llamadas a Stays que puede costar UNA lectura. */
export const RANGO_TARIFAS_MAX_TRAMOS = 7;

/** `STAYS_CALENDARIO_TRAMO_MAX_DIAS`: días que abarca cada una de esas llamadas. */
export const RANGO_TARIFAS_TRAMO_MAX_DIAS = 90;

/**
 * Días que la lectura previa agrega al rango que se edita: `applyMetaToProperty`
 * lee desde la primera noche − 1 hasta la última + 2, para poder detectar si el
 * write tocó una noche vecina que no debía.
 */
export const RANGO_TARIFAS_MARGEN_LECTURA = 3;

/** Días entre la primera y la última noche que el backend alcanza a leer. */
export const RANGO_TARIFAS_MAX_DIAS =
  RANGO_TARIFAS_MAX_TRAMOS * RANGO_TARIFAS_TRAMO_MAX_DIAS - RANGO_TARIFAS_MARGEN_LECTURA;

/** Lo mismo contado en noches, que es como lo dice el modal («Se editarán N noches»). */
export const RANGO_TARIFAS_MAX_NOCHES = RANGO_TARIFAS_MAX_DIAS + 1;

/**
 * Meses que caben SEGURO en el tope, para poder decirlo en el mensaje: ningún
 * mes de calendario pasa de 31 días. Se deriva para que subir o bajar el tope no
 * deje el número viejo en el texto.
 */
const MESES_EN_EL_TOPE = Math.floor(RANGO_TARIFAS_MAX_NOCHES / 31);

/**
 * Lo que ve el admin mientras elige las fechas. No habla de tramos ni de
 * llamadas —no son cosa suya— y no lo manda a revisar lo que escribió: el rango
 * que pidió puede ser perfectamente razonable (una temporada completa) y el que
 * no da es el lector del calendario de Stays.
 */
export const MENSAJE_RANGO_TARIFAS_MAXIMO =
  `El cambio de tarifas alcanza hasta ${RANGO_TARIFAS_MAX_NOCHES} noches de una vez ` +
  `(unos ${MESES_EN_EL_TOPE} meses). Este rango es más largo: acórtalo o hazlo en dos partes.`;

const MS_POR_DIA = 86_400_000;

/**
 * Número de día UTC de una fecha del selector: se arma con el año, el mes y el
 * día que muestra el picker, así que no lo mueven ni la hora ni los cambios de
 * horario de Chile (restar dos medianoches locales que cruzan un cambio de
 * horario da 90 días menos una hora, y eso redondeado es 89).
 */
const numeroDeDia = (fecha: Dayjs): number =>
  Date.UTC(fecha.year(), fecha.month(), fecha.date()) / MS_POR_DIA;

/**
 * ¿El rango elegido pasa de lo que el backend alcanza a leer? `hasta` puede
 * llegar hasta `desde` + RANGO_TARIFAS_MAX_DIAS, inclusive.
 *
 * Mide las dos puntas del selector. El backend mide entre la primera y la última
 * noche REALMENTE seleccionada, y un filtro por día de la semana («solo viernes
 * y sábados») recorta las dos puntas, así que un rango que este aviso rechaza
 * puede caber igual en la lectura del backend. Es a propósito: el aviso queda
 * del lado conservador —nunca deja pasar un rango que el backend vaya a
 * rechazar—, y la diferencia solo aparece en los últimos días de un tope de más
 * de 20 meses.
 *
 * Tampoco mira el campo ni el valor, así que también avisa con «precio base $0»,
 * el único caso que el backend NO lee (crea bloqueos en vez de escribir precios,
 * ver modulo-arriendos-precios.md). Bloquear de más ahí es deliberado: un $0 de
 * más de 20 meses no es un caso real, y atar el aviso al valor tipeado haría que
 * el borde se mueva mientras el admin escribe.
 *
 * Un rango incompleto o inválido no se mide: eso no es un problema de tope (lo
 * atajan las reglas del propio formulario).
 */
export function excedeRangoTarifas(
  desde: Dayjs | null | undefined,
  hasta: Dayjs | null | undefined,
): boolean {
  if (!desde?.isValid() || !hasta?.isValid()) return false;
  return numeroDeDia(hasta) - numeroDeDia(desde) > RANGO_TARIFAS_MAX_DIAS;
}
